import { Stack, Text, Title } from "@mantine/core";
import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { QuietButton } from "@shared/components/buttons/QuietButton";

interface Props {
  /** Headline. Pass either pre-formatted text or an i18n element. */
  title?: ReactNode;
  /** Supporting copy. */
  message?: ReactNode;
  /** When provided, renders a Retry button that invokes this handler. */
  onRetry?: () => void;
}

/**
 * Friendly recoverable-error surface.
 *
 * Used by route-level error boundaries and the router's `notFoundComponent`.
 * In production we surface a calm headline + retry affordance instead of the
 * raw stack — DESIGN.md §1 (editorial restraint) and §6 (no jarring colors).
 */
export function ErrorView({ title, message, onRetry }: Props) {
  return (
    <Stack align="center" gap={16} py={96} role="alert">
      <Title
        order={2}
        style={{
          fontFamily: '"Fraunces Variable", ui-serif, Georgia, serif',
          fontSize: 32,
          fontWeight: 400,
          color: "var(--ink)",
          margin: 0,
        }}
      >
        {title ?? (
          <FormattedMessage id="error.generic.title" defaultMessage="Something went wrong." />
        )}
      </Title>
      <Text c="var(--ink-muted)" style={{ maxWidth: 480, textAlign: "center" }}>
        {message ?? (
          <FormattedMessage
            id="error.generic.message"
            defaultMessage="We hit an unexpected problem. Try again, or refresh the page."
          />
        )}
      </Text>
      {onRetry ? (
        <QuietButton onClick={onRetry}>
          <FormattedMessage id="error.generic.retry" defaultMessage="Retry" />
        </QuietButton>
      ) : null}
    </Stack>
  );
}
